import './promptui-sentry';
import React, { useState, useEffect } from 'react';
import './theme.css';
import Navbar from './components/Navbar';
import StatsCards from './components/StatsCards';
import CandidateList from './components/CandidateList';
import PipelineView from './components/PipelineView';
import JobList from './components/JobList';
import ChatPanel from './components/ChatPanel';
import Tabs from './components/ui/Tabs';
import { Users as _lr_Users, Briefcase as _lr_Briefcase, GitBranch as _lr_GitBranch, MessageSquare as _lr_MessageSquare, LayoutDashboard as _lr_LayoutDashboard, LogIn as _lr_LogIn } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const Users = _lr_Users || (() => null);
const Briefcase = _lr_Briefcase || (() => null);
const GitBranch = _lr_GitBranch || (() => null);
const MessageSquare = _lr_MessageSquare || (() => null);
const LayoutDashboard = _lr_LayoutDashboard || (() => null);
const LogIn = _lr_LogIn || (() => null);
const TABS = [
  { id: 'pipeline', label: 'Pipeline', icon: <GitBranch size={15} /> },
  { id: 'candidates', label: 'Candidates', icon: <Users size={15} /> },
  { id: 'jobs', label: 'Open Roles', icon: <Briefcase size={15} /> },
  { id: 'chat', label: 'AI Screener', icon: <MessageSquare size={15} /> },
  { id: 'admin', label: 'Admin Panel', icon: <LayoutDashboard size={15} /> },
];

export default function App() {
  const [activeTab, setActiveTab] = useState('pipeline');
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [loginEmail, setLoginEmail] = useState('');

  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <motion.div 
          initial={{ opacity: 1, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white p-8 rounded-2xl shadow-xl w-full max-w-md border border-gray-100"
        >
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-purple-200">
              <Briefcase className="text-white" size={32} />
            </div>
            <h1 className="text-2xl font-bold text-gray-900">HireBot AI</h1>
            <p className="text-gray-500">Sign in to manage your recruitment pipeline</p>
          </div>
          <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); setIsLoggedIn(true); }}>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
              <input 
                type="email" 
                required
                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none"
                placeholder="recruiter@company.com"
                value={loginEmail}
                onChange={(e) => setLoginEmail(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
              <input 
                type="password" 
                required
                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none"
                placeholder="••••••••"
              />
            </div>
            <button 
              type="submit"
              className="w-full bg-purple-600 hover:bg-purple-700 text-white font-semibold py-2.5 rounded-lg transition-colors flex items-center justify-center gap-2"
            >
              <LogIn size={18} /> Sign In
            </button>
          </form>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" style={{ fontFamily: 'var(--font-body)' }}>
      <Navbar userEmail={loginEmail} onLogout={() => setIsLoggedIn(false)} />
      <main className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        <StatsCards />
        <Tabs tabs={TABS} activeTab={activeTab} onTabChange={setActiveTab} />
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 1, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
          >
            {activeTab === 'pipeline' && <PipelineView />}
            {activeTab === 'candidates' && <CandidateList />}
            {activeTab === 'jobs' && <JobList />}
            {activeTab === 'chat' && <ChatPanel />}
            {activeTab === 'admin' && (
              <div className="bg-white p-8 rounded-xl border border-gray-200 text-center">
                <LayoutDashboard size={48} className="mx-auto text-purple-200 mb-4" />
                <h2 className="text-xl font-bold text-gray-900">Admin Dashboard</h2>
                <p className="text-gray-500 max-w-md mx-auto mt-2">Configure AI screening parameters, manage recruiter seats, and view system-wide analytics.</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8">
                  {['API Keys', 'Team Access', 'Billing'].map(item => (
                    <div key={item} className="p-4 border border-gray-100 rounded-lg hover:bg-purple-50 cursor-pointer transition-colors">
                      <p className="font-semibold text-purple-700">{item}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </main>
    </div>
  );
}
